// Forecast filters component

import React from 'react';
import { FailureFilters } from '@/types/filters';
import { Workspace } from '@/types/workspace';
import { useFilterOptions } from '@/hooks/useFailures';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { FilterTags } from '@/components/filters/FilterTags';

interface ForecastFiltersProps {
  filters: FailureFilters;
  onFiltersChange: (filters: FailureFilters) => void;
  sourceWorkspace: Workspace | null;
  disabled: boolean;
}

export const ForecastFilters: React.FC<ForecastFiltersProps> = ({
  filters,
  onFiltersChange,
  sourceWorkspace,
  disabled
}) => {
  // Get actual filter options from the same API used by analysis
  const { data: filterOptions, isLoading: filterOptionsLoading } = useFilterOptions();

  const handleFilterChange = (key: keyof FailureFilters, value: unknown) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  // Get available options from filter API
  const availableSites = filterOptions?.sites || [];
  const availableComponentsMajor = filterOptions?.componentsMajor || [];
  const availableTurbineModels = filterOptions?.turbineModels || [];

  // Extract source analysis filters to enforce subset constraint
  const sourceFilters = sourceWorkspace?.configuration?.filters || {};
  const sourceSites = sourceFilters.sites || [];
  const sourceComponents = sourceFilters.componentsMajor || [];
  const sourceTurbineModels = sourceFilters.turbineModels || [];

  // Helper function to check if option is allowed (subset of source analysis)
  const isOptionAllowed = (option: string, sourceList: string[]) => {
    // If source analysis had no filter (empty array), all options are allowed
    if (sourceList.length === 0) return true;
    // Otherwise, only options that were in the source analysis are allowed
    return sourceList.includes(option);
  };

  // Filter options to only show allowed subset options
  const allowedSites = availableSites.filter(site => isOptionAllowed(site, sourceSites));
  const allowedComponents = availableComponentsMajor.filter(comp => isOptionAllowed(comp, sourceComponents));
  const allowedTurbineModels = availableTurbineModels.filter(model => isOptionAllowed(model, sourceTurbineModels));

  // Generate filter tags for selected filters
  const generateFilterTags = () => {
    const tags: Array<{
      id: string;
      category: string;
      label: string;
      value: string;
      color: string;
    }> = [];

    // Site tags
    if (filters.sites && filters.sites.length > 0) {
      filters.sites.forEach(site => {
        tags.push({
          id: `site-${site}`,
          category: 'sites',
          label: 'Site',
          value: site,
          color: 'blue'
        });
      });
    }

    // Component tags
    if (filters.componentsMajor && filters.componentsMajor.length > 0) {
      filters.componentsMajor.forEach(component => {
        tags.push({
          id: `component-${component}`,
          category: 'componentsMajor',
          label: 'Component',
          value: component,
          color: 'green'
        });
      });
    }

    // Turbine model tags
    if (filters.turbineModels && filters.turbineModels.length > 0) {
      filters.turbineModels.forEach(model => {
        tags.push({
          id: `model-${model}`,
          category: 'turbineModels',
          label: 'Model',
          value: model,
          color: 'purple'
        });
      });
    }
    return tags;
  };

  const handleRemoveTag = (tagId: string) => {
    const tag = generateFilterTags().find(t => t.id === tagId);
    if (!tag) return;

    if (tag.category === 'sites') {
      const newSites = (filters.sites || []).filter(site => `site-${site}` !== tagId);
      handleFilterChange('sites', newSites);
    } else if (tag.category === 'componentsMajor') {
      const newComponents = (filters.componentsMajor || []).filter(comp => `component-${comp}` !== tagId);
      handleFilterChange('componentsMajor', newComponents);
    } else if (tag.category === 'turbineModels') {
      const newModels = (filters.turbineModels || []).filter(model => `model-${model}` !== tagId);
      handleFilterChange('turbineModels', newModels);
    }
  };

  const handleClearAllFilters = () => {
    onFiltersChange({
      ...filters,
      sites: [],
      componentsMajor: [],
      turbineModels: [],
    });
  };

  return (
    <div className="space-y-6">
      {/* Filter Dropdowns */}
      <div className={`space-y-4 ${disabled || filterOptionsLoading ? 'opacity-50 pointer-events-none' : ''}`}>
        
        {/* Sites Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sites {filterOptionsLoading && <span className="text-xs text-gray-500">(Loading...)</span>}
          </label>
          <MultiSelectDropdown
            options={allowedSites.map(site => ({ value: site, label: site }))}
            selectedValues={filters.sites || []}
            onChange={(values) => handleFilterChange('sites', values)}
            placeholder={`Select sites (${allowedSites.length} available)`}
            disabled={allowedSites.length === 0}
            className="w-full"
          />
          {sourceSites.length > 0 && allowedSites.length < availableSites.length && (
            <p className="mt-1 text-xs text-gray-500">
              {availableSites.length - allowedSites.length} sites excluded (not in source analysis)
            </p>
          )}
        </div>

        {/* Components Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Major Components {filterOptionsLoading && <span className="text-xs text-gray-500">(Loading...)</span>}
          </label>
          <MultiSelectDropdown
            options={allowedComponents.map(comp => ({ value: comp, label: comp }))}
            selectedValues={filters.componentsMajor || []}
            onChange={(values) => handleFilterChange('componentsMajor', values)}
            placeholder={`Select components (${allowedComponents.length} available)`}
            disabled={allowedComponents.length === 0}
            className="w-full"
          />
          {sourceComponents.length > 0 && allowedComponents.length < availableComponentsMajor.length && (
            <p className="mt-1 text-xs text-gray-500">
              {availableComponentsMajor.length - allowedComponents.length} components excluded (not in source analysis)
            </p>
          )}
        </div>

        {/* Turbine Models Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Turbine Models {filterOptionsLoading && <span className="text-xs text-gray-500">(Loading...)</span>}
          </label>
          <MultiSelectDropdown
            options={allowedTurbineModels.map(model => ({ value: model, label: model }))}
            selectedValues={filters.turbineModels || []}
            onChange={(values) => handleFilterChange('turbineModels', values)}
            placeholder={`Select models (${allowedTurbineModels.length} available)`}
            disabled={allowedTurbineModels.length === 0}
            className="w-full"
          />
          {sourceTurbineModels.length > 0 && allowedTurbineModels.length < availableTurbineModels.length && (
            <p className="mt-1 text-xs text-gray-500">
              {availableTurbineModels.length - allowedTurbineModels.length} models excluded (not in source analysis)
            </p>
          )}
        </div>
      </div>

      {/* Filter Tags */}
      <div>
        <FilterTags
          tags={generateFilterTags()}
          onRemoveTag={handleRemoveTag}
          onClearAll={handleClearAllFilters}
          showClearAll={generateFilterTags().length > 0}
        />
      </div>
    </div>
  );
};
